export const translations = {
  en: {
    ui: {
      place: 'Software engineer · Tokyo',
      enlarge: 'Enlarge',
      skip: 'Skip to content',
      langLabel: '日本語',
      // Captions for the photos that hang off a whole section rather than a
      // record; the per-record ones live with the record they belong to.
      photos: {
        about: { alt: 'Aso', caption: 'Aso' },
        research: { alt: 'Research' },
        outside: { alt: 'Monaco Grand Prix', caption: 'Monaco GP' },
      },
      titles: {
        now: 'About',
        work: 'Work',
        study: 'Education',
        made: 'Projects',
        papers: 'Publications',
        grants: 'Scholarships & grants',
        margins: 'Outside work',
        hello: 'Contact',
      },
    },
    hero: {
      tagline:
        'Software Development Engineer at Amazon, with experience in AI, robotics, and product engineering across startups and research.',
    },
    about: {
      intro:
        'I enjoy working across software and hardware, and turning technology into things people actually use.',
      margins:
        'I play violin, take photographs, and follow Formula 1.',
    },
    experience: {
      edu1: {
        date: 'Apr 2023 – Mar 2026',
        school: 'the University of Tokyo',
        degree: 'M.Eng · Nakao Laboratory',
        detail: 'Localization of beacon networks via Graph Neural Network.',
        story:
          'My research was on UAV positioning in GNSS-denied environments. Here I worked on locating the beacon network itself, rather than the drone: the beacons are the infrastructure the drone’s positioning depends on, so their own positions have to be known first. I worked with LoRa and RSSI, and developed a physics-aware Graph Neural Network to make the estimates more robust.',
      },
      edu2: {
        date: 'Mar 2023 – Jul 2023',
        school: 'Università di Trento',
        degree: 'Erasmus+ ICM exchange · Granelli Laboratory',
        detail: 'UAV localization using LoRa beacon network.',
        photoCaption: 'Università di Trento, 2023',
        story:
          'I joined Granelli Lab on an Erasmus exchange and worked on LoRa-based localization for drones. This was the other side of the problem from my master’s: here I was locating the drone itself, using the beacons as references. I improved the method and tested it both in simulation and in the field.',
      },
      edu3: {
        date: 'Apr 2019 – Mar 2023',
        school: 'the University of Tokyo',
        degree: 'B.Eng · Mizuno Laboratory',
        detail: 'Energy-efficient relay algorithms for LPWA sensing.',
        story:
          'I worked on LPWA communication for real-time sensing, together with Nakao Lab. My focus was the relay algorithm: proposing and evaluating a way of passing data through the network that uses less energy, and testing it on real hardware. I also studied Spanish through the university’s Trilingual Program, and played in the Philomusica Symphony Orchestra.',
      },
      work1: {
        date: 'Apr 2026 – Present',
        company: 'Amazon',
        position: 'Software Development Engineer · Tokyo',
        detail: 'Building the deals you see on the books and manga pages.',
        story:
          'I joined Amazon Japan as a new graduate Software Development Engineer. I work on deals for the books and manga pages of the Japanese marketplace.',
      },
      work2: {
        date: 'Apr 2025 – Jan 2026',
        company: 'ENIAQ',
        position: 'Full Stack Engineer · contract, Tokyo',
        detail: 'Shipped AI products to some of Japan’s largest companies.',
        story:
          'ENIAQ is a startup out of the University of Tokyo. I worked as a full stack engineer on AI products, and worked directly with enterprise customers, including one of Japan’s largest real estate companies and a major cloud business solutions provider.',
        link: {
          url: 'https://prtimes.jp/main/html/rd/p/000000009.000156296.html',
          label: 'Press release',
        },
      },
      work3: {
        date: 'Oct 2023 – Feb 2024',
        company: 'OSARO',
        position: 'Solutions Support Engineer · intern, San Francisco',
        detail: 'Moved to San Francisco to keep working on the robots.',
        photoCaption: 'OSARO, San Francisco, 2023',
        story:
          'I moved to San Francisco to continue my internship at OSARO, an AI robotics startup working on warehouse automation. I worked on depalletizing robot integration and on improving depth camera accuracy, and kept up the system integration and vision model work I had started in Tokyo. I also demoed our systems to visitors, including customers and investors.',
      },
      work4: {
        date: 'Aug 2021 – Sep 2023',
        company: 'OSARO',
        position: 'Solutions Support Engineer · intern, Tokyo / remote',
        detail: 'Integrated warehouse robot deployments, from cell layout to vision models.',
        story:
          'I joined OSARO as an intern while I was studying. I designed system layouts and worked on hardware and software integration for warehouse robotics deployments. I improved vision models using real warehouse data and custom data processing pipelines, and built demos for trade shows.',
      },
    },
    grants: {
      grant1: {
        date: '2024 – 2025',
        name: 'NEDO Entrepreneurs Program',
        org: 'New Energy and Industrial Technology Development Organization',
        amount: '¥3,000,000',
      },
      grant2: {
        date: '2023',
        name: 'Erasmus+ ICM Mobility Grant',
        org: 'European Union',
        amount: '€4,900',
      },
      grant3: {
        date: '2022',
        name: 'Scholarship',
        org: 'KEYENCE Foundation',
        amount: '¥300,000',
      },
    },
    publications: {
      viewPaper: 'Read the paper',
      pub1: {
        date: '2024',
        title:
          'LoRa-based Localization for Drones: Methodological Enhancements Explored through Simulations and Real-World Experiments',
        authors: 'K. Hirotsu, F. Granelli and A. Nakao',
        cite: 'IEEE Access, vol. 12, pp. 145988-145996, 2024',
        url: 'https://ieeexplore.ieee.org/document/10683691',
      },
      pub2: {
        date: '2023',
        title:
          'Proposal and Empirical Evaluation of Relay Communication Methods for LPWA Real-time Sensing',
        authors: 'K. Hirotsu, K. Mizuno, S. Tabeta, T. Shimada, Y. Fujimoto, A. Nakao',
        cite: 'IEICE Technical Report, vol. 122, no. 406, pp. 481-486',
        url: 'https://ken.ieice.org/ken/paper/20230303aCSp/eng/',
      },
    },
    projects: {
      viewWebsite: 'Visit the site',
      viewMore: 'More on GitHub',
      showMore: 'Show more projects',
      showLess: 'Show fewer',
      project1: {
        date: '2024 – 2025',
        title: 'Autonomous cycle counting drone',
        description: 'A drone that counts warehouse inventory without a pilot.',
        photoCaption: 'Cycle counting drone, 2024',
        story:
          'We prototyped a drone that flies through a warehouse and counts inventory on its own. It received a ¥3,000,000 grant from the NEDO Entrepreneurs Program, and we conducted a proof of concept experiment with a large logistics company. My time was split between engineering and the business side. The engineering was mostly about scanning codes reliably in bad conditions. The business side was customer interviews, site visits, and meetings with potential investors and customers.',
      },
      project2: {
        date: '2024',
        title: 'AI home renovation assistant',
        description: 'Generates interior design based on your style.',
        story:
          'It generates interior design based on your style, and suggests real products you can buy, so the result is something you can actually order rather than just a picture.',
        link: 'https://naiso-ai.vercel.app/',
      },
      project3: {
        date: '2023',
        title: 'AI floorplan scale analysis',
        description: 'Finds the scale of a floorplan and builds it in 3D.',
        story:
          'Floorplans usually don’t come with a scale, so it is hard to tell if your furniture will fit. This finds the scale, generates a life-sized 3D model, and lets you walk through it in VR. It won several awards at JPHACKS.',
        link: 'https://oyster-ashy.vercel.app/',
      },
      project4: {
        date: '2022',
        title: 'Serving order optimizer',
        description: 'Decides which dish should leave the kitchen first.',
        story:
          'It uses a thermal camera to check the temperature of each dish and work out the best order to serve them in, so nothing arrives cold. It won the Audience and Best Tech awards.',
      },
      project5: {
        date: '2022',
        title: 'Autonomous boat',
        description: 'Finds a marker, navigates to it, and docks itself.',
        story:
          'A boat that detects AprilTag markers, navigates toward them, and connects to the dock with a gripper when it arrives.',
      },
      project7: {
        date: '2022',
        title: 'Autonomous rover',
        description: 'An RC car that drives a route on its own.',
        story:
          'A modified RC car running ArduRover. You give it a route as a set of GPS waypoints and it drives it on its own, with nobody on the controller. It can also be driven by hand over the internet rather than by radio, which takes off the few-hundred-metre limit an RC link normally has.',
      },
      project6: {
        date: '2022',
        title: 'Film camera FPV drone',
        description: 'Carries and controls a film camera in the air.',
        story:
          'I 3D printed a frame for my drone to carry a film camera and remotely shoot aerial photos.',
      },
    },
    contact: {
      subtitle:
        'Feel free to contact me about anything, by email or through any of the links below.',
    },
    footer: {
      location: 'Tokyo, Japan',
      set: 'Set in EB Garamond',
    },
  },

  ja: {
    ui: {
      place: 'ソフトウェアエンジニア ・ 東京',
      enlarge: '拡大',
      skip: '本文へ',
      langLabel: 'English',
      photos: {
        about: { alt: '阿蘇', caption: '阿蘇' },
        research: { alt: '研究' },
        outside: { alt: 'モナコグランプリ', caption: 'モナコGP' },
      },
      titles: {
        now: '自己紹介',
        work: '職歴',
        study: '学歴',
        made: 'プロジェクト',
        papers: '論文',
        grants: '奨学金・助成金',
        margins: '仕事以外のこと',
        hello: 'お問い合わせ',
      },
    },
    hero: {
      tagline:
        'AmazonのSoftware Development Engineerです。これまでスタートアップや研究で、AIやロボティクスなどを活用した開発に取り組んできました。',
    },
    about: {
      intro:
        'ソフトウェアからハードウェアまで幅広く関わりながら、技術を実際に使われる形にしていくのが好きです。',
      margins:
        'バイオリンを弾いたり、写真を撮ったり、Formula 1を見るのが趣味です。',
    },
    experience: {
      edu1: {
        date: '2023/04 – 2026/03',
        school: '東京大学大学院',
        degree: '修士（工学）· 中尾研究室',
        detail: '測位の基盤となるビーコンネットワークの位置推定。',
        story:
          'GNSSが使えない環境でのUAVの測位について研究しました。ここでは機体そのものではなく、ビーコンネットワーク自体の位置推定に取り組みました。ビーコンはUAVの測位を支えるインフラなので、まずその位置が分かっている必要があります。LoRaとRSSIを用い、物理情報を取り入れたグラフニューラルネットワークを開発して、推定のロバスト性を高めました。',
      },
      edu2: {
        date: '2023/03 – 2023/07',
        school: 'トレント大学',
        degree: '交換留学 (Erasmus+ICM) · Granelli 研究室',
        detail: 'LoRaビーコンを使ったUAVの測位。',
        photoCaption: 'トレント大学・2023年',
        story:
          'Erasmusの交換留学でGranelli研究室に所属し、LoRaを用いたドローンの測位に取り組みました。修士の研究とは逆側の問題で、ここではビーコンを基準として機体そのものの位置を求めています。手法を改良し、シミュレーションと実際の屋外実験の両方で検証しました。',
      },
      edu3: {
        date: '2019/04 – 2023/03',
        school: '東京大学',
        degree: '学士（工学）· 水野研究室',
        detail: 'LPWAセンシングのための省電力なリレーアルゴリズム。',
        story:
          '中尾研究室と連携し、リアルタイムセンシング向けのLPWA通信を研究しました。特に取り組んだのはリレーアルゴリズムで、消費電力を抑えてデータを中継する方式を提案し、実機で評価しました。在学中はTLPでスペイン語も学び、フィロムジカ交響楽団に所属していました。',
      },
      work1: {
        date: '2026/04 – 現在',
        company: 'Amazon',
        position: 'Software Development Engineer · 東京',
        detail: '書籍ページのセール関連の開発。',
        story:
          'Amazon Japanに新卒のSoftware Development Engineerとして入社しました。日本の書籍ページのセール関連の開発を担当しています。',
      },
      work2: {
        date: '2025/04 – 2026/01',
        company: 'ENIAQ',
        position: 'Full Stack Engineer · 業務委託、東京',
        detail: '日本有数の大手企業用のAIサービス開発。',
        story:
          'ENIAQは東京大学発のスタートアップです。フルスタックエンジニアとしてAIサービスの開発を担当し、日本有数の大手不動産企業や、クラウド型業務ソリューションを提供する大手企業と直接やりとりしながら開発しました。',
        link: {
          url: 'https://prtimes.jp/main/html/rd/p/000000009.000156296.html',
          label: 'プレスリリース',
        },
      },
      work3: {
        date: '2023/11 – 2024/02',
        company: 'OSARO',
        position: 'Solutions Support Engineer · インターン、San Francisco',
        detail: 'アメリカにて開発業務を継続。',
        photoCaption: 'OSARO・サンフランシスコ・2023年',
        story:
          '倉庫の自動化に取り組むAIロボティクススタートアップのOSAROで、サンフランシスコに移ってインターンを続けました。デパレタイジングロボットのインテグレーションや深度カメラの精度改善を担当し、東京で始めたシステム設計やビジョンモデルの改善も継続しました。来訪した顧客や投資家へのデモも担当しました。',
      },
      work4: {
        date: '2021/08 – 2023/09',
        company: 'OSARO',
        position: 'Solutions Support Engineer · インターン、東京 / リモート',
        detail: '日本市場での倉庫用ロボットアーム導入。',
        story:
          '大学に通いながらインターンとして働きました。倉庫用ロボットアームの導入に向けたシステムレイアウトの設計や、ハードウェアとソフトウェアのインテグレーションを担当しました。実際の商品のデータと独自の処理パイプラインを使ってビジョンモデルを改善し、展示会向けのデモも作りました。',
      },
    },
    grants: {
      grant1: {
        date: '2024 – 2025',
        name: 'NEDO Entrepreneurs Program',
        org: '新エネルギー・産業技術総合開発機構',
        amount: '¥3,000,000',
      },
      grant2: {
        date: '2023',
        name: 'Erasmus+ICM Mobility Grant',
        org: 'European Union',
        amount: '€4,900',
      },
      grant3: {
        date: '2022',
        name: '奨学金',
        org: 'キーエンス財団',
        amount: '¥300,000',
      },
    },
    publications: {
      viewPaper: '論文を読む',
      pub1: {
        date: '2024',
        title:
          'LoRa-based Localization for Drones: Methodological Enhancements Explored through Simulations and Real-World Experiments',
        authors: 'K. Hirotsu, F. Granelli and A. Nakao',
        cite: 'IEEE Access, vol. 12, pp. 145988-145996, 2024',
        url: 'https://ieeexplore.ieee.org/document/10683691',
      },
      pub2: {
        date: '2023',
        title:
          'Proposal and Empirical Evaluation of Relay Communication Methods for LPWA Real-time Sensing',
        authors: 'K. Hirotsu, K. Mizuno, S. Tabeta, T. Shimada, Y. Fujimoto, A. Nakao',
        cite: 'IEICE Technical Report, vol. 122, no. 406, pp. 481-486',
        url: 'https://ken.ieice.org/ken/paper/20230303aCSp/eng/',
      },
    },
    projects: {
      viewWebsite: 'サイトを見る',
      viewMore: 'GitHubで他のものも見る',
      showMore: '他のプロジェクトを見る',
      showLess: '閉じる',
      project1: {
        date: '2024 – 2025',
        title: '自動棚卸ドローン',
        description: '倉庫の在庫を自動で数えるドローン。',
        photoCaption: '自動棚卸ドローン・2024年',
        story:
          '倉庫の中を自律で飛び、在庫を数えるドローンのプロトタイプを作りました。NEDO Entrepreneurs Programから300万円の助成を受け、大手物流企業との実証実験まで進めました。技術面では、悪条件でもコードを確実に読み取ることが中心で、事業面では、顧客へのヒアリングや倉庫の現場訪問、投資家との対話などを行いました。',
      },
      project2: {
        date: '2024',
        title: 'AIリノベーションアシスタント',
        description: '好みに合わせてインテリアを提案。',
        story:
          '好みのスタイルからインテリアデザインを生成し、実際に購入できる商品を提案します。画像を見て終わりではなく、そのまま買えるようにすることを目指しました。',
        link: 'https://naiso-ai.vercel.app/',
      },
      project3: {
        date: '2023',
        title: 'AI間取り図分析',
        description: '間取り図の縮尺を求めて3Dに変換。',
        story:
          '間取り図には縮尺が書かれていないことが多く、家具が入るかどうか判断できません。縮尺を計算して実物大の3Dモデルを生成し、VRで歩けるようにしました。JPHACKSで複数の賞をいただきました。',
        link: 'https://oyster-ashy.vercel.app/',
      },
      project4: {
        date: '2022',
        title: '提供順序最適化システム',
        description: 'どの料理から運ぶかを決めるシステム。',
        story:
          'サーマルカメラで料理の温度を測り、冷めないように提供する順番を決めます。オーディエンス賞とベストテック賞をいただきました。',
      },
      project5: {
        date: '2022',
        title: '自律航行ボート',
        description: 'マーカーを見つけて自分で接岸するボート。',
        story:
          'AprilTagのマーカーを検出して自律で航行し、到着するとグリッパーでドックに接続するボートです。',
      },
      project7: {
        date: '2022',
        title: '自律走行ローバー',
        description: '指定した経路を自動で走るRCカー。',
        story:
          'RCカーを改造し、ArduRoverで動かしています。GPSのウェイポイントで経路を与えると、操縦なしでその通りに走ります。電波ではなくインターネット経由で手動操縦することもできます。ラジコンの数百メートルという制限がなくなり、別の国からでも走らせられます。',
      },
      project6: {
        date: '2022',
        title: 'フィルムカメラFPVドローン',
        description: 'フィルムカメラをドローンに載せて空撮。',
        story:
          'カスタムフレームを設計・3Dプリントしてフィルムカメラを載せ、遠隔操作で空撮を可能にしました。',
      },
    },
    contact: {
      subtitle:
        'どんなことでも、メールやSNSからお気軽にご連絡ください。',
    },
    footer: {
      location: 'Tokyo, Japan',
      set: 'EB Garamond 組み',
    },
  },
};
